import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending"); // pending | paid | expired | error
  const attempts = useRef(0);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 10) {
        setStatus("error");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        // continue polling
      }
      setTimeout(poll, 2000);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <main className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="fleuron mb-6">❦</div>
      {status === "pending" && (
        <>
          <h1 className="font-display text-4xl">Heating the wax…</h1>
          <p className="font-body text-ink-iron mt-4">Confirming your arrangement with Stripe.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <h1 className="font-display text-4xl text-wax-red">The seal is fixed.</h1>
          <p className="font-body text-ink-iron mt-4 text-lg">
            Welcome, kindly. Your subscription is active. Your hermitage may now begin in earnest.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <button onClick={() => navigate("/dashboard")} data-testid="success-to-dashboard" className="wax-button">To the dashboard</button>
            <Link to="/browse/hermitages" className="ghost-button">Browse hermitages</Link>
          </div>
        </>
      )}
      {status === "expired" && (
        <>
          <h1 className="font-display text-4xl">The session lapsed.</h1>
          <p className="font-body text-ink-iron mt-4">Please try again from the pricing ledger.</p>
          <Link to="/pricing" className="wax-button mt-6 inline-block">Back to pricing</Link>
        </>
      )}
      {status === "error" && (
        <>
          <h1 className="font-display text-4xl">A small misadventure.</h1>
          <p className="font-body text-ink-iron mt-4">We couldn't verify the payment. If you were charged, kindly contact hello@hearthandhermit.com.</p>
          <Link to="/pricing" className="ghost-button mt-6 inline-block">Back to pricing</Link>
        </>
      )}
    </main>
  );
}
